import { useDebounce } from "@uidotdev/usehooks";
import { useEffect, useRef, useState } from "react";

interface UseSyncedDebouncedValueOptions<T> {
  delay?: number;
  normalize?: (value: T) => T;
  isEqual?: (a: T, b: T) => boolean;
}

interface UseSyncedDebouncedValueReturn<T> {
  value: T;
  setValue: (next: T) => void;
  debouncedValue: T;
}

/**
 * Keeps a local piece of state in sync with a prop (typically filters coming
 * from the server) while also exposing a debounced version of that value for
 * side-effects such as firing network requests.
 */
export const DEFAULT_FILTER_DEBOUNCE_MS = 750;

export function useSyncedDebouncedValue<T>(
  externalValue: T,
  options?: UseSyncedDebouncedValueOptions<T>,
): UseSyncedDebouncedValueReturn<T> {
  const { delay = DEFAULT_FILTER_DEBOUNCE_MS, normalize, isEqual = Object.is } =
    options ?? {};

  // Store functions in refs to prevent re-renders when they change reference
  const normalizeRef = useRef(normalize);
  const isEqualRef = useRef(isEqual);

  // Update refs when functions change (allows hot updates if needed)
  useEffect(() => {
    normalizeRef.current = normalize;
    isEqualRef.current = isEqual;
  }, [normalize, isEqual]);

  const normalizeValue = (value: T): T =>
    normalizeRef.current ? normalizeRef.current(value) : value;

  const [value, setValue] = useState<T>(() => normalizeValue(externalValue));

  useEffect(() => {
    const next = normalizeValue(externalValue);
    setValue((current) => (isEqualRef.current(current, next) ? current : next));
  }, [externalValue]); // Only depend on externalValue, not the functions

  const debouncedValue = useDebounce(value, delay);

  return {
    value,
    setValue,
    debouncedValue,
  };
}
